import * as React from 'react';
import { Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchApi, GCacheRecord, makeResetCache, notify } from 'gossamer-front-end';
import { BookingViewRow, EventExtendedViewRow } from 'cb-model';
import { Countdown } from './Countdown';

interface EventBookedCardProps {
  booking: BookingViewRow;
  eventRecord: GCacheRecord<EventExtendedViewRow>;
  userId: string;
}

export const EventBookedCard = (props: EventBookedCardProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const [submitting, setSubmitting] = React.useState(false);
  const [_click, setClick] = React.useState(0);

  const now = new Date();
  const eventStart = props.eventRecord.data?.start_at ? new Date(props.eventRecord.data.start_at) : null;
  const eventEnd = props.eventRecord.data?.end_at ? new Date(props.eventRecord.data.end_at) : null;
  const isOnDemand = props.eventRecord.data?.type === 'on-demand';
  const isUpcoming = !isOnDemand && !!eventStart && now < eventStart;
  const isCurrentlyHappening = !!eventStart && !!eventEnd && eventStart <= now && now <= eventEnd;
  const isPast = !isOnDemand && !!eventEnd && now > eventEnd;
  const didAttend = props.booking.attendance_result === 'attended';

  React.useEffect(() => {
    const intervals = [eventStart, eventEnd]
      .filter((d) => !!d)
      .map((d) => (d as Date).getTime() - now.getTime())
      .filter((diff) => diff > 0)
      .map((diff) => setTimeout(() => setClick((prev) => prev + 1), diff));
    return () => intervals.forEach((timeout) => clearTimeout(timeout));
  }, [eventStart, eventEnd]);

  const isCancellable =
    props.eventRecord.data?.booking_method === 'careerbook' &&
    props.eventRecord.data?.visibility_limit !== 'closed' &&
    (isUpcoming || isOnDemand);

  const handleCancelBooking = async () => {
    setSubmitting(true);
    try {
      await fetchApi('POST', '/main/unbook-user-from-event-by-event-id', { eventId: props.eventRecord.data?.id });
      resetCache('BookingView');
      notify(['Booking cancelled', 'warning']);
    } catch (err: any) {
      notify([err?.message || 'Cancellation failed', 'danger']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <Card.Header>
        Booking status:&nbsp;<strong>Booked</strong>
      </Card.Header>
      <Card.Body>
        {isUpcoming && (
          <Countdown
            startDate={props.eventRecord.data?.start_at || ''}
            venue={props.eventRecord.data?.location || ''}
          />
        )}
        {isOnDemand && <p>This is an on-demand event. You can watch it at any time.</p>}
        {isCurrentlyHappening && <p>This event is currently happening.</p>}
        {isPast && <p>This event has already taken place.</p>}
        {isCancellable && (
          <Button variant="outline-danger" size="sm" onClick={handleCancelBooking} disabled={submitting}>
            {submitting ? 'Cancelling...' : 'Cancel booking'}
          </Button>
        )}
        {(!isUpcoming || isOnDemand) &&
          !props.booking.attendance_id &&
          ['attendanceOnly', 'full'].includes(props.eventRecord.data?.flow_option || '') && (
            <>
              <p>
                You can register your attendance by scanning a QR code that should be provided by your host if you
                attended.
              </p>
              <p>Alternatively, ask your host in person to provide an electronic signature by clicking here:</p>
              <Link to={`/website/attend-sign/${props.booking.ticket_booking_id}`}>
                <Button variant="primary" size="sm">
                  Request Host Signature
                </Button>
              </Link>
            </>
          )}
        {(!isUpcoming || isOnDemand) &&
          ((didAttend && props.eventRecord.data?.flow_option === 'full') ||
            props.eventRecord.data?.flow_option === 'noAttendance') &&
          !props.booking.event_feedback_id && (
            <>
              <p>Thank you for attending this event! Please click here to leave feedback:</p>
              <Link to={`/website/event-feedback/${props.eventRecord.data?.id}`}>
                <Button variant="primary" size="sm">
                  Leave Feedback
                </Button>
              </Link>
            </>
          )}
      </Card.Body>
    </Card>
  );
};
